import { useState, useEffect, useRef, useCallback } from 'react';
import GlobalLayout from '../GlobalLayout';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Slider from '@mui/material/Slider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import BarChartIcon from '@mui/icons-material/BarChart';
import PaletteIcon from '@mui/icons-material/Palette';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import FilterChooser from '../../components/filters/FilterChooser';
import { downloadMedia, getMediaList, getQueryList } from '../../services/front_api';
import ChartBuilder from '../../components/charts/ChartBuilder';
import { usePdfElements } from '../../hooks/usePdfElements';
import { useInteract } from '../../hooks/useInteract';
import { generatePDF } from '../../utils/pdfGenerator';
import './create.css';

const DEFAULT_CHART_FONT_SCALE = 1.0;
const CHART_TITLE_FONT_BASE = 24;
const CHART_LEGEND_FONT_BASE = 13;

const getQueryCategory = (query) => query?.category || query?.type || 'chart';
const findQueryById = (queries, queryId) => queries.find((query) => String(query.id) === String(queryId));
const getChartTypeForQuery = (query, currentType = 'bar') => {
    if (getQueryCategory(query) === 'indic') {
        return 'indic';
    }

    return currentType === 'indic' ? 'bar' : (currentType || 'bar');
};

const getChartFontScale = (element) => element?.fontScale ?? DEFAULT_CHART_FONT_SCALE;
const getChartTitleFontSize = (element) => element?.titleFontSize ?? CHART_TITLE_FONT_BASE;
const getChartLegendFontSize = (element) => element?.legendFontSize ?? CHART_LEGEND_FONT_BASE;

const IMAGE_MEDIA_EXTENSIONS = ['jpg', 'jpeg', 'png', 'svg', 'webp', 'gif'];
const PDF_IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

const normalizeCollection = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.media)) return response.media;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.results)) return response.results;
    return [];
};

const normalizeTypeText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const getMediaId = (media) => media?.id ?? media?.media_id ?? media?.mediaId ?? media?.uuid ?? '';

const getMediaName = (media) => (
    media?.label
    || media?.title
    || media?.name
    || media?.filename
    || media?.file_name
    || media?.original_name
    || media?.originalName
    || (getMediaId(media) ? `Média #${getMediaId(media)}` : 'Média')
);

const getMediaMime = (media) => media?.mime_type || media?.mimeType || media?.content_type || media?.contentType || '';

const getMediaTypeLabel = (media) => {
    const mediaType = media?.media_type ?? media?.mediaType;

    if (mediaType && typeof mediaType === 'object') {
        return mediaType.label || mediaType.name || mediaType.code || mediaType.slug || mediaType.value || '';
    }

    return mediaType || media?.category || media?.kind || media?.type || '';
};

const getMediaExtension = (media) => String(getMediaName(media)).split('.').pop()?.toLowerCase() || '';

const isImageMedia = (media) => {
    const mimeType = String(getMediaMime(media)).toLowerCase();
    const typeLabel = normalizeTypeText(getMediaTypeLabel(media));
    const mediaTypeId = Number(media?.media_type_id ?? media?.mediaTypeId ?? media?.media_type?.id ?? media?.mediaType?.id);

    return mimeType.startsWith('image/')
        || typeLabel.includes('image')
        || mediaTypeId === 1
        || IMAGE_MEDIA_EXTENSIONS.includes(getMediaExtension(media));
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const loadImage = (src) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
});

const rasterizeImageDataUrl = async (dataUrl) => {
    const image = await loadImage(dataUrl);
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;

    if (!canvas.width || !canvas.height) {
        throw new Error('Image sans dimensions exploitables');
    }

    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/png');
};

const blobToPdfImageDataUrl = async (blob) => {
    const dataUrl = await blobToDataUrl(blob);

    if (PDF_IMAGE_MIME_TYPES.includes(String(blob.type || '').toLowerCase())) {
        return dataUrl;
    }

    try {
        return await rasterizeImageDataUrl(dataUrl);
    } catch (error) {
        console.error('Erreur lors de la conversion de l’image pour le PDF:', error);
        return dataUrl;
    }
};

const PDFCreator = () => {
    const {
        elements,
        setElements,
        selectedElement,
        setSelectedElement,
        addElement,
        removeElement,
        updateElementContent,
        bringToFront,
        sendToBack,
        bringForward,
        sendBackward
    } = usePdfElements();

    const canvasRef = useRef(null);
    const imagePreviewRunRef = useRef(0);
    const { alignmentGuides, elementRefs } = useInteract(canvasRef, elements, setElements, addElement);
    const [queries, setQueries] = useState([]);
    const [imageMedias, setImageMedias] = useState([]);
    const [imageMediaPreviews, setImageMediaPreviews] = useState({});
    const [imagePreviewLoadingIds, setImagePreviewLoadingIds] = useState([]);
    const [imagePreviewErrorIds, setImagePreviewErrorIds] = useState([]);
    const [imageMediasLoading, setImageMediasLoading] = useState(false);
    const [imageMediasError, setImageMediasError] = useState('');
    const [imageSelectionLoading, setImageSelectionLoading] = useState(null);
    const [showFilterModal, setShowFilterModal] = useState(false);

    // Format A4 en pixels (à 72 DPI)
    const CANVAS_WIDTH = 595;
    const CANVAS_HEIGHT = 842;

    const fetchImageMedias = useCallback(async () => {
        const runId = imagePreviewRunRef.current + 1;
        imagePreviewRunRef.current = runId;
        setImageMediasLoading(true);
        setImageMediasError('');
        setImageMediaPreviews({});
        setImagePreviewErrorIds([]);

        try {
            const response = await getMediaList();
            const medias = normalizeCollection(response).filter(isImageMedia);
            const mediaIds = medias.map((media) => String(getMediaId(media))).filter(Boolean);

            setImageMedias(medias);
            setImagePreviewLoadingIds(mediaIds);

            medias.forEach(async (media) => {
                const mediaId = getMediaId(media);
                if (!mediaId) return;

                const mediaIdKey = String(mediaId);

                try {
                    const blob = await downloadMedia(mediaId);
                    const imageSrc = await blobToPdfImageDataUrl(blob);

                    if (imagePreviewRunRef.current !== runId) return;

                    setImageMediaPreviews((currentPreviews) => ({
                        ...currentPreviews,
                        [mediaIdKey]: imageSrc
                    }));
                } catch (error) {
                    console.error(`Erreur lors du chargement de l'aperçu du média ${mediaId}:`, error);

                    if (imagePreviewRunRef.current !== runId) return;

                    setImagePreviewErrorIds((currentIds) => (
                        currentIds.includes(mediaIdKey) ? currentIds : [...currentIds, mediaIdKey]
                    ));
                } finally {
                    if (imagePreviewRunRef.current === runId) {
                        setImagePreviewLoadingIds((currentIds) => currentIds.filter((currentId) => currentId !== mediaIdKey));
                    }
                }
            });
        } catch (error) {
            console.error('Erreur lors du chargement des médias image:', error);
            setImageMedias([]);
            setImageMediaPreviews({});
            setImagePreviewLoadingIds([]);
            setImagePreviewErrorIds([]);
            setImageMediasError('Impossible de charger les images disponibles.');
        } finally {
            setImageMediasLoading(false);
        }
    }, []);

    // Charger la liste des queries au montage
    useEffect(() => {
        const fetchQueries = async () => {
            try {
                const response = await getQueryList(); // Charger toutes les queries
                setQueries(response || []);
            } catch (error) {
                console.error('Erreur lors du chargement des queries:', error);
            }
        };
        fetchQueries();
    }, []);

    useEffect(() => {
        fetchImageMedias();
    }, [fetchImageMedias]);

    const handleCloseFilterModal = () => {
        if (selectedElementData && selectedElementData.type === 'chart') {
            const storageKey = `pdf_element_${selectedElementData.id}`;
            const storedParams = localStorage.getItem(storageKey);
            if (storedParams) {
                try {
                    const params = JSON.parse(storedParams);
                    updateElementContent(selectedElementData.id, 'params', params);
                } catch (e) {
                    console.error("Error parsing filters", e);
                }
            }
        }
        setShowFilterModal(false);
    };

    const handleGeneratePDF = () => {
        generatePDF(elements, canvasRef, elementRefs, CANVAS_WIDTH, CANVAS_HEIGHT);
    };

    const handleChartQueryChange = (elementId, queryId) => {
        const selectedQuery = findQueryById(queries, queryId);

        setElements((currentElements) => currentElements.map((element) => (
            element.id === elementId
                ? {
                    ...element,
                    queryId,
                    chartType: getChartTypeForQuery(selectedQuery, element.chartType)
                }
                : element
        )));
    };

    const handleImageMediaChange = async (elementId, mediaId) => {
        if (!mediaId) {
            setElements((currentElements) => currentElements.map((element) => (
                element.id === elementId
                    ? {
                        ...element,
                        mediaId: '',
                        imageSrc: '',
                        imageName: '',
                        imageMime: ''
                    }
                    : element
            )));
            return;
        }

        const mediaIdKey = String(mediaId);
        const selectedMedia = imageMedias.find((media) => String(getMediaId(media)) === mediaIdKey);

        setImageSelectionLoading({ elementId, mediaId: mediaIdKey });
        setImageMediasError('');

        try {
            let imageSrc = imageMediaPreviews[mediaIdKey];
            let imageMime = selectedMedia ? getMediaMime(selectedMedia) : '';

            if (!imageSrc) {
                const blob = await downloadMedia(mediaId);
                imageSrc = await blobToPdfImageDataUrl(blob);
                imageMime = blob.type || imageMime;

                setImageMediaPreviews((currentPreviews) => ({
                    ...currentPreviews,
                    [mediaIdKey]: imageSrc
                }));
            }

            setElements((currentElements) => currentElements.map((element) => (
                element.id === elementId
                    ? {
                        ...element,
                        mediaId: mediaIdKey,
                        imageSrc,
                        imageName: selectedMedia ? getMediaName(selectedMedia) : `Média #${mediaIdKey}`,
                        imageMime
                    }
                    : element
            )));
        } catch (error) {
            console.error(`Erreur lors du chargement du média ${mediaId}:`, error);
            setImageMediasError('Impossible de charger cette image.');
        } finally {
            setImageSelectionLoading(null);
        }
    };

    const selectedElementData = elements.find(el => el.id === selectedElement);
    const selectedImageUpdating = Boolean(
        selectedElementData
        && imageSelectionLoading?.elementId === selectedElementData.id
    );
    const selectedImageMediaId = selectedElementData?.type === 'image' && selectedElementData.mediaId
        ? String(selectedElementData.mediaId)
        : '';
    const selectedImageMediaInList = imageMedias.some((media) => String(getMediaId(media)) === selectedImageMediaId);

    return (
        <GlobalLayout>
            <div style={{ padding: '1rem' }}>
                <h1>Création de PDF</h1>

                <Grid container spacing={2}>
                    {/* Palette d'éléments */}
                    <Grid item xs={12} md={2}>
                        <div style={{
                            padding: '1rem',
                            background: '#f5f5f5',
                            borderRadius: 4,
                            position: 'sticky',
                            top: 20,
                            zIndex: 10
                        }}>
                            <h3 style={{ marginTop: 0 }}>Blocs</h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20, alignItems: 'center' }}>
                                <Tooltip title="Texte" placement="right">
                                    <div
                                        className="draggable-item"
                                        data-type="text"
                                        style={{
                                            padding: '12px',
                                            background: '#fff',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 8,
                                            cursor: 'move',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                            width: 'fit-content'
                                        }}
                                    >
                                        <TextFieldsIcon color="primary" />
                                    </div>
                                </Tooltip>

                                <Tooltip title="Image" placement="right">
                                    <div
                                        className="draggable-item"
                                        data-type="image"
                                        style={{
                                            padding: '12px',
                                            background: '#fff',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 8,
                                            cursor: 'move',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                            width: 'fit-content'
                                        }}
                                    >
                                        <ImageIcon color="success" />
                                    </div>
                                </Tooltip>

                                <Tooltip title="Graphique" placement="right">
                                    <div
                                        className="draggable-item"
                                        data-type="chart"
                                        style={{
                                            padding: '12px',
                                            background: '#fff',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 8,
                                            cursor: 'move',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                            width: 'fit-content'
                                        }}
                                    >
                                        <BarChartIcon style={{ color: '#FF9800' }} />
                                    </div>
                                </Tooltip>

                                <Tooltip title="Bloc Couleur" placement="right">
                                    <div
                                        className="draggable-item"
                                        data-type="colorbloc"
                                        style={{
                                            padding: '12px',
                                            background: '#fff',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 8,
                                            cursor: 'move',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                            width: 'fit-content'
                                        }}
                                    >
                                        <PaletteIcon style={{ color: '#9C27B0' }} />
                                    </div>
                                </Tooltip>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                <Tooltip title="Générer PDF" placement="right">
                                    <div
                                        onClick={handleGeneratePDF}
                                        style={{
                                            padding: '12px',
                                            background: '#2196F3',
                                            color: '#fff',
                                            borderRadius: 8,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            width: 'fit-content',
                                            marginTop: 10
                                        }}
                                    >
                                        <PictureAsPdfIcon />
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                    </Grid>

                    {/* Canvas */}
                    <Grid item xs={12} md={7}>
                        <div
                            ref={canvasRef}
                            className="pdf-canvas-container"
                            style={{
                                width: CANVAS_WIDTH,
                                height: CANVAS_HEIGHT,
                                background: '#fff',
                                border: '1px solid #ccc',
                                position: 'relative',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                margin: '0 auto'
                            }}
                        >
                            {/* Guides d'alignement */}
                            {alignmentGuides.map((guide, index) => (
                                <div
                                    key={index}
                                    className={`alignment-guide ${guide.type}`}
                                    style={{
                                        [guide.type === 'horizontal' ? 'top' : 'left']: `${guide.position}px`
                                    }}
                                />
                            ))}

                            {elements.map(element => (
                                <div
                                    key={element.id}
                                    ref={el => elementRefs.current[element.id] = el}
                                    className="canvas-element"
                                    data-id={element.id}
                                    data-x={element.x}
                                    data-y={element.y}
                                    onMouseDown={() => setSelectedElement(element.id)}
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        width: `${element.width}px`,
                                        height: `${element.height}px`,
                                        transform: `translate(${element.x}px, ${element.y}px)`,
                                        border: selectedElement === element.id ? '2px solid #2196F3' : '1px solid #ccc',
                                        opacity: element.opacity !== undefined ? element.opacity : 1.0,
                                        background: element.type === 'text' ? 'rgba(33, 150, 243, 0.05)' :
                                            element.type === 'image' ? 'rgba(76, 175, 80, 0.05)' :
                                                element.type === 'colorbloc' ? (element.backgroundColor || '#2196F3') :
                                                    'rgba(255, 152, 0, 0.05)',
                                        padding: '8px',
                                        cursor: 'move',
                                        boxSizing: 'border-box',
                                        overflow: 'hidden',
                                        touchAction: 'none',
                                        userSelect: 'none'
                                    }}
                                >
                                    {element.type === 'text' && (
                                        <div style={{
                                            fontSize: element.fontSize,
                                            color: element.color,
                                            wordWrap: 'break-word',
                                            pointerEvents: 'none',
                                            textAlign: element.textAlign || 'left',
                                            fontWeight: element.fontWeight || 'normal',
                                            fontStyle: element.fontStyle || 'normal',
                                            textDecoration: element.textDecoration || 'none',
                                            width: '100%',
                                            height: '100%'
                                        }}>
                                            {element.content}
                                        </div>
                                    )}
                                    {element.type === 'image' && (
                                        element.imageSrc ? (
                                            <img
                                                src={element.imageSrc}
                                                alt={element.imageName || 'Image'}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    pointerEvents: 'none'
                                                }}
                                            />
                                        ) : (
                                            <div style={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 6,
                                                color: '#4CAF50',
                                                pointerEvents: 'none',
                                                textAlign: 'center',
                                                fontSize: 12
                                            }}>
                                                <ImageIcon fontSize="small" />
                                                <span>Sélectionner une image</span>
                                            </div>
                                        )
                                    )}
                                    {element.type === 'chart' && (
                                        <div
                                            className="chart-content-wrapper"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                pointerEvents: 'none',
                                                overflow: 'hidden',
                                                position: 'relative'
                                            }}>
                                            {element.queryId ? (
                                                (() => {
                                                    const selectedQuery = findQueryById(queries, element.queryId);
                                                    return (
                                                        <ChartBuilder
                                                            view={getChartTypeForQuery(selectedQuery, element.chartType)}
                                                            query={selectedQuery?.name || ''}
                                                            category={getQueryCategory(selectedQuery)}
                                                            params={element.params}
                                                            scale={element.fontScale ?? DEFAULT_CHART_FONT_SCALE}
                                                            titleFontSize={element.titleFontSize}
                                                            legendFontSize={element.legendFontSize}
                                                        />
                                                    );
                                                })()
                                            ) : (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    color: '#999'
                                                }}>
                                                    📊 Chart (Sélectionner une query)
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {element.type === 'colorbloc' && (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            pointerEvents: 'none'
                                        }}>
                                            {/* Le fond de couleur est déjà appliqué via le style du parent */}
                                        </div>
                                    )}
                                    <button
                                        className="element-delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeElement(element.id);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: 2,
                                            right: 2,
                                            background: 'red',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: 3,
                                            cursor: 'pointer',
                                            padding: '2px 6px',
                                            fontSize: 12
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    </Grid>

                    {/* Panneau de propriétés */}
                    <Grid item xs={12} md={3}>
                        <div style={{
                            padding: '1rem',
                            background: '#f5f5f5',
                            borderRadius: 4,
                            position: 'sticky',
                            top: 20
                        }}>
                            <h3 style={{ marginTop: 0 }}>Propriétés</h3>
                            {selectedElementData ? (
                                <div>
                                    <p><strong>Type:</strong> {selectedElementData.type}</p>

                                    {selectedElementData.type === 'text' && (
                                        <>
                                            <TextField
                                                label="Contenu"
                                                multiline
                                                rows={4}
                                                fullWidth
                                                value={selectedElementData.content}
                                                onChange={(e) => updateElementContent(selectedElementData.id, 'content', e.target.value)}
                                                style={{ marginBottom: 10 }}
                                            />
                                            <TextField
                                                label="Taille de police"
                                                type="number"
                                                fullWidth
                                                value={selectedElementData.fontSize}
                                                onChange={(e) => updateElementContent(selectedElementData.id, 'fontSize', parseInt(e.target.value))}
                                                style={{ marginBottom: 10 }}
                                            />
                                            <TextField
                                                label="Couleur du texte"
                                                type="color"
                                                fullWidth
                                                value={selectedElementData.color || '#000000'}
                                                onChange={(e) => updateElementContent(selectedElementData.id, 'color', e.target.value)}
                                                style={{ marginBottom: 10 }}
                                            />

                                            <FormControl fullWidth style={{ marginBottom: 10 }}>
                                                <InputLabel id="text-align-label">Alignement</InputLabel>
                                                <Select
                                                    labelId="text-align-label"
                                                    value={selectedElementData.textAlign || 'left'}
                                                    label="Alignement"
                                                    onChange={(e) => updateElementContent(selectedElementData.id, 'textAlign', e.target.value)}
                                                >
                                                    <MenuItem value="left">Gauche</MenuItem>
                                                    <MenuItem value="center">Centré</MenuItem>
                                                    <MenuItem value="right">Droite</MenuItem>
                                                    <MenuItem value="justify">Justifié</MenuItem>
                                                </Select>
                                            </FormControl>

                                            <div style={{ marginBottom: 10 }}>
                                                <Typography variant="caption" display="block" gutterBottom>Style</Typography>
                                                <ToggleButtonGroup
                                                    value={[
                                                        selectedElementData.fontWeight === 'bold' ? 'bold' : null,
                                                        selectedElementData.fontStyle === 'italic' ? 'italic' : null,
                                                        selectedElementData.textDecoration === 'underline' ? 'underline' : null,
                                                    ].filter(Boolean)}
                                                    onChange={(e, newFormats) => {
                                                        updateElementContent(selectedElementData.id, 'fontWeight', newFormats.includes('bold') ? 'bold' : 'normal');
                                                        updateElementContent(selectedElementData.id, 'fontStyle', newFormats.includes('italic') ? 'italic' : 'normal');
                                                        updateElementContent(selectedElementData.id, 'textDecoration', newFormats.includes('underline') ? 'underline' : 'none');
                                                    }}
                                                    aria-label="text formatting"
                                                    size="small"
                                                    fullWidth
                                                >
                                                    <ToggleButton value="bold" aria-label="bold">
                                                        <b>B</b>
                                                    </ToggleButton>
                                                    <ToggleButton value="italic" aria-label="italic">
                                                        <i>I</i>
                                                    </ToggleButton>
                                                    <ToggleButton value="underline" aria-label="underline">
                                                        <u>U</u>
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            </div>
                                        </>
                                    )}

                                    {selectedElementData.type === 'image' && (
                                        <>
                                            <Typography variant="caption" display="block" gutterBottom>
                                                Images disponibles
                                            </Typography>

                                            {imageMediasLoading && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                                    <CircularProgress size={16} />
                                                    <Typography variant="caption">Chargement des images...</Typography>
                                                </div>
                                            )}

                                            {imageMediasError && (
                                                <Typography variant="caption" color="error" display="block" style={{ marginBottom: 10 }}>
                                                    {imageMediasError}
                                                </Typography>
                                            )}

                                            {selectedElementData.imageSrc && !selectedImageMediaInList && !selectedImageUpdating && (
                                                <div style={{
                                                    marginBottom: 10,
                                                    border: '2px solid #1976d2',
                                                    borderRadius: 4,
                                                    overflow: 'hidden',
                                                    background: '#fff'
                                                }}>
                                                    <img
                                                        src={selectedElementData.imageSrc}
                                                        alt={selectedElementData.imageName || 'Image sélectionnée'}
                                                        style={{
                                                            display: 'block',
                                                            width: '100%',
                                                            maxHeight: 96,
                                                            objectFit: 'contain',
                                                            background: '#fafafa'
                                                        }}
                                                    />
                                                    <Typography variant="caption" display="block" style={{ padding: '6px 8px', color: '#666' }}>
                                                        {selectedElementData.imageName || `Média #${selectedImageMediaId}`}
                                                    </Typography>
                                                </div>
                                            )}

                                            {!imageMediasLoading && imageMedias.length === 0 && (
                                                <Typography variant="caption" display="block" style={{ marginBottom: 10, color: '#666' }}>
                                                    Aucune image disponible.
                                                </Typography>
                                            )}

                                            {imageMedias.length > 0 && (
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                                    gap: 6,
                                                    maxHeight: 260,
                                                    overflowY: 'auto',
                                                    padding: 1,
                                                    marginBottom: 8
                                                }}>
                                                    {imageMedias.map((media) => {
                                                        const mediaId = getMediaId(media);
                                                        if (!mediaId) return null;

                                                        const mediaIdKey = String(mediaId);
                                                        const previewSrc = imageMediaPreviews[mediaIdKey];
                                                        const previewLoading = imagePreviewLoadingIds.includes(mediaIdKey);
                                                        const previewError = imagePreviewErrorIds.includes(mediaIdKey);
                                                        const selected = selectedImageMediaId === mediaIdKey;
                                                        const selecting = selectedImageUpdating && imageSelectionLoading?.mediaId === mediaIdKey;

                                                        return (
                                                            <Tooltip key={mediaIdKey} title={getMediaName(media)}>
                                                                <span style={{ display: 'block' }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleImageMediaChange(selectedElementData.id, mediaIdKey)}
                                                                        disabled={selectedImageUpdating || (previewLoading && !previewSrc)}
                                                                        aria-pressed={selected}
                                                                        style={{
                                                                            width: '100%',
                                                                            minWidth: 0,
                                                                            padding: 3,
                                                                            border: `2px solid ${selected ? '#1976d2' : 'transparent'}`,
                                                                            outline: `1px solid ${selected ? '#90caf9' : '#d7dde5'}`,
                                                                            borderRadius: 3,
                                                                            background: selected ? '#e3f2fd' : '#fff',
                                                                            color: '#111827',
                                                                            cursor: selectedImageUpdating || (previewLoading && !previewSrc) ? 'default' : 'pointer',
                                                                            opacity: selectedImageUpdating && !selecting ? 0.62 : 1,
                                                                            boxSizing: 'border-box',
                                                                            textAlign: 'left'
                                                                        }}
                                                                    >
                                                                        <div style={{
                                                                            aspectRatio: '1 / 1',
                                                                            borderRadius: 3,
                                                                            background: '#f3f4f6',
                                                                            overflow: 'hidden',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            position: 'relative',
                                                                            marginBottom: 3
                                                                        }}>
                                                                            {previewSrc && (
                                                                                <img
                                                                                    src={previewSrc}
                                                                                    alt={getMediaName(media)}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        objectFit: 'cover',
                                                                                        display: 'block'
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            {!previewSrc && previewLoading && <CircularProgress size={14} />}
                                                                            {!previewSrc && !previewLoading && (
                                                                                <div style={{
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    alignItems: 'center',
                                                                                    gap: 2,
                                                                                    color: previewError ? '#b91c1c' : '#6b7280',
                                                                                    fontSize: 10,
                                                                                    textAlign: 'center',
                                                                                    padding: 3
                                                                                }}>
                                                                                    <ImageIcon style={{ fontSize: 16 }} />
                                                                                    <span>{previewError ? 'Aperçu indisponible' : 'Aperçu'}</span>
                                                                                </div>
                                                                            )}
                                                                            {selecting && (
                                                                                <div style={{
                                                                                    position: 'absolute',
                                                                                    inset: 0,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    background: 'rgba(255, 255, 255, 0.68)'
                                                                                }}>
                                                                                    <CircularProgress size={14} />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <span style={{
                                                                            display: 'block',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: 10,
                                                                            fontWeight: selected ? 700 : 500
                                                                        }}>
                                                                            {getMediaName(media)}
                                                                        </span>
                                                                    </button>
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {selectedImageMediaId && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="error"
                                                    fullWidth
                                                    onClick={() => handleImageMediaChange(selectedElementData.id, '')}
                                                    disabled={selectedImageUpdating}
                                                    style={{ marginBottom: 10 }}
                                                >
                                                    Retirer l'image
                                                </Button>
                                            )}

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                onClick={fetchImageMedias}
                                                disabled={imageMediasLoading || selectedImageUpdating}
                                                style={{ marginBottom: 20 }}
                                            >
                                                Actualiser les images
                                            </Button>
                                        </>
                                    )}

                                    {selectedElementData.type === 'chart' && (
                                        <>
                                            <FormControl fullWidth style={{ marginBottom: 10 }}>
                                                <InputLabel id="query-select-label">Query</InputLabel>
                                                <Select
                                                    labelId="query-select-label"
                                                    value={selectedElementData.queryId || ''}
                                                    label="Query"
                                                    onChange={(e) => handleChartQueryChange(selectedElementData.id, e.target.value)}
                                                >
                                                    <MenuItem value="">
                                                        <em>Sélectionner une query</em>
                                                    </MenuItem>
                                                    {queries.map((query) => (
                                                        <MenuItem key={query.id} value={query.id}>
                                                            {query.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <FormControl fullWidth style={{ marginBottom: 10 }}>
                                                <InputLabel id="chart-type-label">Type de graphique</InputLabel>
                                                <Select
                                                    labelId="chart-type-label"
                                                    value={getChartTypeForQuery(findQueryById(queries, selectedElementData.queryId), selectedElementData.chartType)}
                                                    label="Type de graphique"
                                                    onChange={(e) => updateElementContent(selectedElementData.id, 'chartType', e.target.value)}
                                                >
                                                    {getQueryCategory(findQueryById(queries, selectedElementData.queryId)) === 'indic' ? (
                                                        <MenuItem value="indic">Indicateur</MenuItem>
                                                    ) : (
                                                        [
                                                            <MenuItem key="bar" value="bar">Barres</MenuItem>,
                                                            <MenuItem key="barLine" value="barLine">Barres + lignes</MenuItem>,
                                                            <MenuItem key="line" value="line">Lignes</MenuItem>,
                                                            <MenuItem key="pie" value="pie">Camembert</MenuItem>,
                                                            <MenuItem key="stackedColumn" value="stackedColumn">Colonnes empilées</MenuItem>,
                                                            <MenuItem key="indic" value="indic">Indicateur</MenuItem>
                                                        ]
                                                    )}
                                                </Select>
                                            </FormControl>

                                            <div style={{ marginBottom: 20 }}>
                                                <Typography gutterBottom>
                                                    Taille des valeurs (axes): {Math.round(getChartFontScale(selectedElementData) * 100)}%
                                                </Typography>
                                                <Slider
                                                    value={getChartFontScale(selectedElementData)}
                                                    onChange={(e, value) => updateElementContent(selectedElementData.id, 'fontScale', value)}
                                                    min={0}
                                                    max={1}
                                                    step={0.05}
                                                    marks={[
                                                        { value: 0, label: '0%' },
                                                        { value: 0.5, label: '50%' },
                                                        { value: 1, label: '100%' }
                                                    ]}
                                                    valueLabelDisplay="auto"
                                                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                                                />
                                            </div>

                                            <div style={{ marginBottom: 20 }}>
                                                <Typography gutterBottom>
                                                    Taille du titre: {getChartTitleFontSize(selectedElementData)} px
                                                </Typography>
                                                <Slider
                                                    value={getChartTitleFontSize(selectedElementData)}
                                                    onChange={(e, value) => updateElementContent(selectedElementData.id, 'titleFontSize', value)}
                                                    min={6}
                                                    max={48}
                                                    step={1}
                                                    marks={[
                                                        { value: 6, label: '6px' },
                                                        { value: 24, label: '24px' },
                                                        { value: 48, label: '48px' }
                                                    ]}
                                                    valueLabelDisplay="auto"
                                                    valueLabelFormat={(value) => `${value}px`}
                                                />
                                            </div>

                                            <div style={{ marginBottom: 20 }}>
                                                <Typography gutterBottom>
                                                    Taille de la légende: {getChartLegendFontSize(selectedElementData)} px
                                                </Typography>
                                                <Slider
                                                    value={getChartLegendFontSize(selectedElementData)}
                                                    onChange={(e, value) => updateElementContent(selectedElementData.id, 'legendFontSize', value)}
                                                    min={6}
                                                    max={32}
                                                    step={1}
                                                    marks={[
                                                        { value: 6, label: '6px' },
                                                        { value: 16, label: '16px' },
                                                        { value: 32, label: '32px' }
                                                    ]}
                                                    valueLabelDisplay="auto"
                                                    valueLabelFormat={(value) => `${value}px`}
                                                />
                                            </div>

                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() => setShowFilterModal(true)}
                                                style={{ marginBottom: 20 }}
                                            >
                                                Configurer Filtres
                                            </Button>
                                        </>
                                    )}

                                    {selectedElementData.type === 'colorbloc' && (
                                        <>
                                            <TextField
                                                label="Couleur de fond"
                                                type="color"
                                                fullWidth
                                                value={selectedElementData.backgroundColor || '#2196F3'}
                                                onChange={(e) => updateElementContent(selectedElementData.id, 'backgroundColor', e.target.value)}
                                                style={{ marginBottom: 10 }}
                                            />
                                        </>
                                    )}

                                    <div style={{ marginBottom: 20 }}>
                                        <Typography gutterBottom>
                                            Opacité: {Math.round((selectedElementData.opacity || 1.0) * 100)}%
                                        </Typography>
                                        <Slider
                                            value={selectedElementData.opacity || 1.0}
                                            onChange={(e, value) => updateElementContent(selectedElementData.id, 'opacity', value)}
                                            min={0}
                                            max={1}
                                            step={0.05}
                                            marks={[
                                                { value: 0, label: '0%' },
                                                { value: 0.5, label: '50%' },
                                                { value: 1, label: '100%' }
                                            ]}
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                                        />
                                    </div>

                                    <div style={{ marginBottom: 20 }}>
                                        <Typography gutterBottom>Disposition</Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Button variant="outlined" size="small" fullWidth onClick={() => bringToFront(selectedElementData.id)}>
                                                    Premier plan
                                                </Button>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Button variant="outlined" size="small" fullWidth onClick={() => sendToBack(selectedElementData.id)}>
                                                    Arrière plan
                                                </Button>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Button variant="outlined" size="small" fullWidth onClick={() => bringForward(selectedElementData.id)}>
                                                    Avancer
                                                </Button>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Button variant="outlined" size="small" fullWidth onClick={() => sendBackward(selectedElementData.id)}>
                                                    Reculer
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </div>

                                    <p style={{ fontSize: 12, color: '#666', marginTop: 20 }}>
                                        Position: {Math.round(selectedElementData.x)}, {Math.round(selectedElementData.y)}<br />
                                        Taille: {Math.round(selectedElementData.width)} × {Math.round(selectedElementData.height)}
                                    </p>

                                    <Button
                                        variant="outlined"
                                        color="error"
                                        fullWidth
                                        onClick={() => removeElement(selectedElementData.id)}
                                        style={{ marginTop: 10 }}
                                    >
                                        Supprimer
                                    </Button>
                                </div>
                            ) : (
                                <p style={{ color: '#999', fontSize: 14 }}>
                                    Sélectionnez un élément pour modifier ses propriétés
                                </p>
                            )}
                        </div>
                    </Grid>
                </Grid>
            </div>

            <Dialog open={showFilterModal} onClose={handleCloseFilterModal} maxWidth="md" fullWidth>
                <DialogTitle>Filtres du graphique</DialogTitle>
                <DialogContent>
                    {selectedElementData && selectedElementData.type === 'chart' && (
                        <FilterChooser
                            query={findQueryById(queries, selectedElementData.queryId)?.name || ''}
                            category={getQueryCategory(findQueryById(queries, selectedElementData.queryId))}
                            storageKey={`pdf_element_${selectedElementData.id}`}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseFilterModal}>Appliquer</Button>
                </DialogActions>
            </Dialog>
        </GlobalLayout >
    );
};

export default PDFCreator;
